import * as React from 'react';
import { DefaultFocusIndicator } from '../styles/FocusIndicators';
import { PreventUserSelect } from '../styles/PreventUserSelect';
import { SwitcherTextBase, SwitcherTextBaseStyle } from './SwitcherTextBase';

export enum SelectedToggle {
  Left,
  Right
}

export interface ToggleTextProps {
  /** The description of the toggle that will be read by screen readers. */
  ariaLabel?: string;
  children?: never;
  className?: string;
  /** The styling of the toggle container.  */
  containerStyle?: React.CSSProperties;
  disabled?: boolean;
  /** Called when left toggle is selected. */
  handleLeftToggleSelected: () => void;
  /** Called when right toggle is selected. */
  handleRightToggleSelected: () => void;
  /** The text of the left toggle. */
  leftToggleText: string;
  /** The `<input>` name attribute. */
  name?: string;
  /** The text of the right toggle. */
  rightToggleText: string;
  /** Which toggle is selected. */
  selectedToggle: SelectedToggle;
  /** Test id to be attached to the toggle. '-{left/right}-{label/input}' will be automatically added as a suffix */
  testId: string;
}

export const SwitcherStyles: SwitcherTextBaseStyle = {
  container: {
    display: 'flex'
  },

  toggleBase: {
    ...PreventUserSelect,
    alignItems: 'center',
    backgroundColor: 'transparent',
    borderStyle: 'solid',
    borderWidth: 1,
    boxSizing: 'border-box',
    color: '#2E2E2E', // component/content/outline/on-light/default
    display: 'flex',
    flexBasis: '100%',
    justifyContent: 'center',
    letterSpacing: '0.2px',
    minHeight: 22,
    padding: `4px 12px`,
    textAlign: 'center', // needed for multiline
  },
  firstToggle: {
    borderBottomLeftRadius: 2,
    borderTopLeftRadius: 2,
  },
  lastToggle: {
    borderBottomRightRadius: 2,
    borderTopRightRadius: 2,
  },
  focusToggle: DefaultFocusIndicator({ inset: true }) as React.CSSProperties,

  selectedToggle: {
    backgroundColor: '#05628A', // component/background/cta/on-light/default
    borderColor: '#05628A', // component/background/cta/on-light/default
    color: '#EAF5FE', // component/content/cta/on-light/default
    fontWeight: 'bold',
  },
  hoverSelectedToggle: {
    backgroundColor: '#023248', // component/background/cta/on-light/hover
    borderColor: '#023248', // component/background/cta/on-light/hover
  },
  activeSelectedToggle: {
    backgroundColor: '#001A28', // component/background/cta/on-light/press
    borderColor: '#001A28', // component/background/cta/on-light/press
    color: '#EAF5FE', // component/content/cta/on-light/default
  },
  disabledSelectedToggle: {
    backgroundColor: 'transparent',
    borderColor: '#C9C9C9', // component/border/outline/on-light/disabled
    color: '#C9C9C9', // component/content/outline/on-light/disabled
  },

  unselectedToggle: {
    borderColor: '#747474', // component/border/outline/on-light/default
  },
  hoverUnselectedToggle: {
    backgroundColor: '#C9C9C9', // component/background/outline/on-light/hover
  },
  activeUnselectedToggle: {
    backgroundColor: '#AEAEAE', // component/background/outline/on-light/press
  },
  disabledUnselectedToggle: {
    backgroundColor: 'transparent',
    borderColor: '#C9C9C9', // component/border/outline/on-light/disabled
    color: '#C9C9C9', // component/content/outline/on-light/disabled
  },
};

export class ToggleTextWidget extends React.PureComponent<ToggleTextProps, {}> {
  private handleChange = (idx: number): void => {
    if (idx === 1) {
      this.props.handleRightToggleSelected?.();
    } else {
      this.props.handleLeftToggleSelected?.();
    }
  }

  public render(): JSX.Element {
    return (
      <SwitcherTextBase
        ariaLabel={this.props.ariaLabel}
        className={this.props.className}
        checkedIndex={this.props.selectedToggle === SelectedToggle.Right ? 1 : 0}
        disabled={this.props.disabled}
        handleChange={this.handleChange}
        name={this.props.name}
        style={{ ...SwitcherStyles, container: { ...SwitcherStyles.container, ...this.props.containerStyle } }}
        testId={this.props.testId}
        toggleTexts={[this.props.leftToggleText, this.props.rightToggleText]}
      />
    );
  }
}
